
import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    TextField,
    Button,
    MenuItem,
    Typography,
    Paper,
    Avatar,
    Checkbox,
    FormControlLabel,
    Divider,
    CircularProgress
} from '@mui/material';
import axios from 'axios';
import apiUrl from '../../Config';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const AddEditOffers = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const offerDetails = location.state || null;
    const [error, setError] = useState('')

    const isEditMode = Boolean(offerDetails);

    const [formData, setFormData] = useState({
        couponCode: '',
        title: '',
        description: '',
        discountType: 'percentage',
        discountValue: '',
        minAmount: '',
        maxDiscountAmount: '',
        usageLimit: '',
        validFrom: '',
        validTo: '',
        discountImage: null,
        isActive: true,
    });

    const [imagePreview, setImagePreview] = useState(null);
    const [imageChanged, setImageChanged] = useState(false);
    const [loading, setLoading] = useState(false)

    // Populate form fields if editing
    useEffect(() => {
        if (isEditMode) {
            const {
                couponCode,
                title,
                description,
                discountType,
                discountValue,
                minAmount,
                maxDiscountAmount,
                usageLimit,
                validFrom,
                validTo,
                discountImage
            } = offerDetails;

            setFormData({
                couponCode: couponCode || '',
                title: title || '',
                description: description || '',
                discountType: discountType || 'percentage',
                discountValue: discountValue || '',
                minAmount: minAmount || '',
                maxDiscountAmount: maxDiscountAmount || '',
                usageLimit: usageLimit || '',
                validFrom: validFrom ? validFrom.slice(0, 10) : '',
                validTo: validTo ? validTo.slice(0, 10) : '',
                discountImage: null,
                isActive: offerDetails.isActive ?? true,
            });

            setImagePreview(discountImage || null); // assuming this is a URL
        }
    }, [isEditMode, offerDetails]);

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        if (name === 'discountImage') {
            const file = files[0];
            setFormData((prev) => ({ ...prev, discountImage: file }));
            setImageChanged(true);

            if (file) {
                const reader = new FileReader();
                reader.onloadend = () => setImagePreview(reader.result);
                reader.readAsDataURL(file);
            } else {
                setImagePreview(null);
            }
        } else if (type === 'checkbox') {
            setFormData((prev) => ({ ...prev, [name]: checked }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true)
        const data = new FormData();

        // Only append image if it's changed
        if (imageChanged && formData.discountImage) {
            data.append('discountImage', formData.discountImage);
        }

        // Append all other fields
        Object.entries(formData).forEach(([key, value]) => {
            if (key !== 'discountImage') {
                data.append(key, value);
            }
        });

        try {
            let response;

            if (isEditMode) {
                response = await axios.put(
                    `${apiUrl}/offers/updateoffer/${offerDetails.id}`,
                    data
                );
            } else {
                response = await axios.post(`${apiUrl}/offers/offers`, data);
            }

            if (response?.data?.status === true) {
                toast.success(response?.data?.message || "Somthing went to wrong, Please try again.")
                navigate(-1)
            }
        } catch (error) {
            setError(error?.response?.data?.message || 'Error submitting form:');
        }
        finally {
            setLoading(false)
        }
    };

    return (
        <Box p={3}>
            <Typography variant="h3" gutterBottom>
                {isEditMode ? 'Edit Offer' : 'Add Offer'}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                name="couponCode"
                                label="Coupon Code"
                                size='small'
                                value={formData.couponCode}
                                onChange={handleChange}
                                required
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                name="title"
                                label="Title"
                                size='small'
                                value={formData.title}
                                onChange={handleChange}
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                select
                                fullWidth
                                name="discountType"
                                label="Discount Type"
                                size='small'
                                value={formData.discountType}
                                onChange={handleChange}
                                required
                            >
                                <MenuItem value="percentage">Percentage</MenuItem>
                                <MenuItem value="fixed">Fixed</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                name="description"
                                label="Description"

                                value={formData.description}
                                onChange={handleChange}
                                required
                            />
                        </Grid>



                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                type="number"
                                name="discountValue"
                                label="Discount Value"
                                size='small'
                                value={formData.discountValue}
                                onChange={handleChange}
                                required
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                type="number"
                                name="minAmount"
                                label="Minimum Amount"
                                size='small'
                                value={formData.minAmount}
                                onChange={handleChange}
                                required
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                type="number"
                                name="maxDiscountAmount"
                                label="Max Discount Amount"
                                size='small'
                                value={formData.maxDiscountAmount}
                                onChange={handleChange}
                                required
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                type="number"
                                name="usageLimit"
                                label="Usage Limit"
                                size='small'
                                value={formData.usageLimit}
                                onChange={handleChange}
                                required
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                type="date"
                                name="validFrom"
                                label="Valid From"
                                size='small'
                                InputLabelProps={{ shrink: true }}
                                value={formData.validFrom}
                                onChange={handleChange}
                                required
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                type="date"
                                name="validTo"
                                label="Valid To"
                                size='small'
                                InputLabelProps={{ shrink: true }}
                                value={formData.validTo}
                                onChange={handleChange}
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            {imagePreview && (

                                <Box display="flex" justifyContent="center">
                                    <Avatar
                                        src={imagePreview}
                                        alt="Preview"
                                        variant="rounded"
                                        sx={{ width: 100, height: 100, mb: 2 }}
                                    />
                                </Box>

                            )}
                            <Button fullWidth variant="outlined" component="label">
                                Upload Image
                                <input
                                    type="file"
                                    name="discountImage"
                                    accept="image/*"

                                    hidden
                                    onChange={handleChange}
                                />
                            </Button>
                        </Grid>




                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display="flex" alignItems="center">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.isActive}
                                            onChange={handleChange}
                                            name="isActive"
                                        />
                                    }
                                    label="Is Active"
                                />
                            </Box>
                        </Grid>

                        {error &&
                            <Typography color='error' variant='body2'>
                                {error}
                            </Typography>
                        }
                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ gap: 3, width: '100%', display: "flex", justifyContent: "flex-end" }}>
                                <Button variant="outlined" color="primary" sx={{ marginRight: '30px' }} onClick={() => navigate(-1)}>
                                    Cancel
                                </Button>
                                {/* <Button type="submit" variant="contained" color="primary" >
                                    {isEditMode ? 'Update Offer' : 'Submit Offer'}
                                </Button> */}
                                <Button type="submit" variant="contained" disabled={loading}>
                                    {loading ? (
                                        <CircularProgress size={24} sx={{ color: '#fff' }} />
                                    ) : (
                                        isEditMode ? 'Update Offer' : 'Submit Offer'
                                    )}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Box>
        </Box>
    );
};

export default AddEditOffers;
